import {ImageModuleBaseData} from './interfaces';
import {getFitPayload} from '../../image-inline-editor/Utils';
import {getCachedImage, clearCachedImage} from '../../util/image/cache';
import {isAnimatedGif} from '../../util/isAnimatedGif';
import ModulWithImage from '../ModulWithImage';
import {getIieSourceImageWidth, getIieSourceImageHeight} from './util';
import Model from '../../image-inline-editor/core/Model';
import IieDefaultPayload from '../../image-inline-editor/core/DefaultPayload';

export function handleSelectImage(
    moduleId: number,
    module: ModulWithImage,
    data: ImageModuleBaseData,
    onDataChange: (newData: ImageModuleBaseData) => void
) {
    const filemanagerSourceCallback = (
        items: Filemanager.Item[],
        successCb: (response: any) => void,
        errorCb: (response: any) => void
    ) => {
        const fmImage = items[0];
        const fmImageUrl = fmImage.getFile();

        const imageKey = 'origImageUrl' + moduleId;
        // clear first in case there are previous versions
        clearCachedImage(imageKey);
        getCachedImage(
            fmImageUrl, imageKey
        ).then((image: HTMLImageElement) => {
            const newData = {} as ImageModuleBaseData;
            newData.origImageUrl = fmImageUrl;
            newData.origImageWidth = image.width;
            newData.origImageHeight = image.height;

            // discard all information about tranformed image
            newData.transformedImageUrl = null;
            newData.transformedImageWidth = null;
            newData.transformedImageHeight = null;
            newData.fmImageId = fmImage.getId();
            newData.fmImageSource = fmImage.getSource().getName();

            isAnimatedGif(newData.origImageUrl, (animated) => {
                newData.imageAnimated = animated;
                if (animated) {
                    newData.imageUrl = newData.origImageUrl;
                }
                const hasParent = module.getParentSection() || module.getMatrixModuleParent();

                if (animated || !hasParent) {
                    // keep the previous height, adjust the width
                    const aspect = image.width / image.height;
                    newData.visualImageWidth = aspect * data.visualImageHeight;
                    if (newData.visualImageWidth > module.getMaxWidth()) {
                        newData.visualImageWidth = module.getMaxWidth();
                        newData.visualImageHeight = newData.visualImageWidth / aspect;
                    }
                    newData.imageWidth = newData.visualImageWidth;
                }

                if (hasParent) {
                    // XXX if for some reason images in sections are smaller than they should be, let's fix it now
                    // It happen for some customers (see BUG-1171)
                    newData.imageWidth = Math.max(data.imageWidth, data.visualImageWidth);
                    newData.imageHeight = Math.max(data.imageHeight, data.visualImageHeight);

                    // keep previous width and height
                    newData.iiePayload = getFitPayload(
                        newData.origImageWidth,
                        newData.origImageHeight,
                        data.visualImageWidth,
                        data.visualImageHeight
                    );
                } else {
                    newData.iiePayload = null;
                }

                onDataChange(newData);

                successCb(items);
            });
        }).catch(e => errorCb(e));
    };

    const sourceConfig = {
        batchSize: 1,
        callback: filemanagerSourceCallback
    };
    diy.editor.filemanager = new diy.editor.Filemanager({
        appName: 'filemanager',
        mode: 'selection',
        maxSelection: 1,
        alreadyExisting: 0,
        sources: {
            storage: sourceConfig,
            cms: sourceConfig,
            bigstock: sourceConfig,
            '123rf': sourceConfig
        },
        callback: () => null
    });
}

export function handleAddLink(data: ImageModuleBaseData, onDataChange: (newData: ImageModuleBaseData) => void) {
    const linkData = {
        href: data.link
    };
    diy.editor.linkSelector.openDialog(linkData, (outputData) => {
        const newData = {} as ImageModuleBaseData;
        newData.link = outputData.href;
        newData.linkTarget = outputData.target;
        newData.linkTitle = outputData.value;
        onDataChange(newData);
    });
}

export function handleResetSize(
    data: ImageModuleBaseData,
    module: ModulWithImage,
    onDataChange: (newData: ImageModuleBaseData) => void
) {
    const sourceImageWidth = getIieSourceImageWidth(data);
    const sourceImageHeight = getIieSourceImageHeight(data);
    const aspectRatio = sourceImageWidth / sourceImageHeight;
    const width = Math.min(sourceImageWidth, module.getMaxWidth());
    const height = width / aspectRatio;

    const model = getIieModel(data, module);
    model.resize(width, height, true);

    const newData = {} as ImageModuleBaseData;
    newData.imageWidth = width;
    newData.visualImageWidth = width;
    newData.imageHeight = height;
    newData.visualImageHeight = height;
    newData.imageFit = width >= module.getMaxWidth();
    newData.iiePayload = IieDefaultPayload;
    onDataChange(newData);
}

export function getIieModel(data: ImageModuleBaseData, module: ModulWithImage) {
    const iiePayload = Object.assign({}, IieDefaultPayload, data.iiePayload);
    const model = new Model(
        iiePayload,
        getIieSourceImageWidth(data), getIieSourceImageHeight(data),
        data.visualImageWidth, data.visualImageHeight,
        module.getMaxWidth()
    );
    return model;
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/module/image/common.ts
 **/